import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  CHECK_LISTING_FAVORITE_STATUS,
  REMOVE_LISTING_FROM_AGENT_FAVORITES,
  REMOVE_LISTING_FROM_BUYER_FAVORITES,
} from "../graphql/favoriteQueries";
import FavoriteModal from "./FavoriteModal";
import { useToast } from "./Toast/toastHook";

const FavoriteButton = ({ listingId, agentId, companyId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toast = useToast(4000);

  // Check if listing is favorited
  const { data, loading, refetch } = useQuery(CHECK_LISTING_FAVORITE_STATUS, {
    variables: { listingId, agentId },
    fetchPolicy: "cache-and-network",
  });

  const [removeAgentFavorite] = useMutation(REMOVE_LISTING_FROM_AGENT_FAVORITES, {
    onCompleted() {
      toast("success", "Removed from your favorites");
      refetch();
    },
    onError(e) {
      toast("error", e.message);
    },
  });

  const [removeBuyerFavorite] = useMutation(REMOVE_LISTING_FROM_BUYER_FAVORITES, {
    onCompleted() {
      toast("success", "Removed from buyer's favorites");
      refetch();
    },
    onError(e) {
      toast("error", e.message);
    },
  });

  const favoriteStatus = data?.checkListingFavoriteStatus;
  const isFavorited = favoriteStatus?.isFavoritedByAgent ||
    (favoriteStatus?.buyerFavorites?.length > 0);

  const handleClick = (e) => {
    e.stopPropagation();

    // If already favorited, show options or remove
    if (isFavorited) {
      // If only favorited by agent, remove directly
      if (favoriteStatus?.isFavoritedByAgent && !favoriteStatus?.buyerFavorites?.length) {
        removeAgentFavorite({
          variables: { listingId, agentId },
        });
        return;
      }
      // If has buyer favorites or both, open modal to manage
    }

    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    refetch();
  };

  if (loading) {
    return (
      <button
        className="p-1 hover:text-primary transition-colors"
        disabled
        title="Loading..."
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          className="w-5 h-5 text-gray-300 animate-pulse"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    );
  }

  return (
    <>
      <button
        className="p-1 hover:text-primary transition-colors"
        onClick={handleClick}
        title={isFavorited ? "Manage favorites" : "Add to favorites"}
      >
        {isFavorited ? (
          // Filled heart
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            className="w-5 h-5 text-red-500"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
              clipRule="evenodd"
            />
          </svg>
        ) : (
          // Outline heart
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            className="w-5 h-5 text-gray-500 hover:text-red-500"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
        )}
      </button>

      <FavoriteModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        listingId={listingId}
        agentId={agentId}
        companyId={companyId}
        favoriteStatus={favoriteStatus}
        onRemoveAgentFavorite={() => {
          removeAgentFavorite({
            variables: { listingId, agentId },
          });
        }}
        onRemoveBuyerFavorite={(buyerId) => {
          removeBuyerFavorite({
            variables: { listingId, buyerId },
          });
        }}
      />
    </>
  );
};

export default FavoriteButton;
